import { Paperclip } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@/components/ui';
import { onboardingApi, type StoredSession } from './onboarding-api';

/** חייב להתאים ל-MAX_UPLOAD_BYTES בשרת; חריגה נדחית שם ב-400. */
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.pdf,.txt,.csv';

/**
 * העלאת מסמכים קיימים של העסק.
 *
 * זה לא "צרף קובץ" גנרי: המערכת קוראת הצעות מחיר שהעסק נתן בעבר
 * והזמנות חומרים מספקים, ומסיקה מהן את המחירון ואת שיעור הרווח.
 * הטקסט בדיאלוג מסביר את זה, אחרת אף אחד לא יטרח.
 */
export function UploadDocumentDialog({
  session,
  onUploaded,
}: {
  session: StoredSession;
  onUploaded: () => void;
}) {
  const { t } = useTranslation(['onboarding', 'common']);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState<'QUOTE' | 'MATERIAL_ORDER'>('QUOTE');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = (f: File | null): void => {
    setError(null);
    if (f && f.size > MAX_BYTES) {
      // נבדק כאן *וגם* בשרת. כאן זה משוב מיידי; שם זה הגבול.
      setError(t('upload.tooLarge', { size: (f.size / 1024 / 1024).toFixed(1) }));
      setFile(null);
      return;
    }
    setFile(f);
  };

  const upload = async (): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await onboardingApi.uploadDocument(session, file, docType);
      toast.success(t('upload.success'), t('upload.successHint'));
      setFile(null);
      setOpen(false);
      onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('upload.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="icon" aria-label={t('upload.attach')}>
          <Paperclip aria-hidden />
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('upload.attach')}</DialogTitle>
          <DialogDescription>
            {t('upload.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <Field label={t('upload.docType')} htmlFor="docType">
            <Select value={docType} onValueChange={(v) => setDocType(v as typeof docType)}>
              <SelectTrigger id="docType" aria-label={t('upload.docType')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="QUOTE">{t('upload.quote')}</SelectItem>
                <SelectItem value="MATERIAL_ORDER">{t('upload.materialOrder')}</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label={t('upload.file')} htmlFor="file" error={error ?? undefined} hint={t('upload.fileHint')}>
            <input
              ref={inputRef}
              id="file"
              type="file"
              accept={ACCEPT}
              onChange={(e) => pick(e.target.files?.[0] ?? null)}
              className="block w-full text-xs text-fg-muted file:me-3 file:rounded-(--radius-sm) file:border-0 file:bg-surface-sunken file:px-3 file:py-2 file:text-xs file:text-fg hover:file:bg-surface-hover"
            />
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button size="sm" loading={busy} disabled={!file} onClick={() => void upload()}>
            {t('upload.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
